import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface DiscountCoupon {
  applicableEntity: any;
  foodMenuItemId: number;
  foodMenuId: number;
  menuTitle: string;
  menuDescription?: string;
  basePrice: number;
  imageUrl?: string;
  cookProfileId: number;
  kitchenName?: string;
  areaCodeId?: number;
}

interface ApiResponse {
  data: DiscountCoupon[];
  couponData: [];
  AvailableCouponsData: [];
  isSuccess: boolean;
  status: number;
}
export interface DiscountCoupon {
  couponCode: string;
  discountAmount: number;
  discountType: 'percentage' | 'flat';
  capAmount: number;
  validUntil: string;
  couponUsed: boolean;
  usageCount: number;
  maxUsesPerUser: number;
  applicability: {
    entity_id: number;
    offer_type: string;
    offer_type_id: number;
    applicable_entity: 'area' | 'cook' | 'customer';
  }[];
}

interface CouponsApiResponse {
  data: {
    count: number;
    status: string;
    coupons: DiscountCoupon[];
    message: string;
    userProfileId: number;
  };
  isSuccess: boolean;
  status: number;
}

interface DiscountCouponState {
  data: DiscountCoupon[];
  couponData: any;
  AvailableCouponsData: any;
  loading: boolean;
  error: string | null;
}
interface FoodMenuApiResponse {
  data: {
    data: DiscountCoupon[];
  };
  isSuccess: boolean;
  status: number;
}

const PORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const MASTERURL = import.meta.env.VITE_API_FOOD_ORDER_URL;

export const fetchDiscountCoupons = createAsyncThunk<
  DiscountCoupon[],
  void,
  { rejectValue: string }
>('discountcoupons/fetchDiscountCoupons', async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<ApiResponse>('/get_all_coupons', PORT, MASTERURL);
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});
export const fetchFoodMenuForCoupon = createAsyncThunk<
  DiscountCoupon[], // what Redux finally gets
  { cookProfileIds?: string; areaCodeIds?: string },
  { rejectValue: string }
>('discountcoupons/fetchFoodMenuForCoupon', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiService.post<FoodMenuApiResponse>(
      '/get_all_food_menu_for_coupon',
      payload,
      PORT,
      MASTERURL,
    );

    // ✅ NOW TS KNOWS THIS STRUCTURE
    return response.data?.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});
export const fetchAvailableCouponsForCart = createAsyncThunk<
  DiscountCoupon[], // ✅ Redux finally gets array
  { payload: any },
  { rejectValue: string }
>('discountcoupons/fetchAvailableCouponsForCart', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiService.post<CouponsApiResponse>(
      '/get_available_coupons_for_cart',
      payload,
      PORT,
      MASTERURL,
    );

    // ✅ Return ONLY coupons array
    return response.data.coupons;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const createDiscountCoupons: any = createAsyncThunk(
  'discountcoupons/createDiscountCoupons',
  async (params: any, thunkAPI) => {
    console.log(params);
    try {
      const response = await apiService.post('create_coupon', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const validateAndApplyCoupon: any = createAsyncThunk(
  'discountcoupons/validateAndApplyCoupon',
  async (params: any, thunkAPI) => {
    try {
      const response = await apiService.post('validate_and_apply_coupon', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const deleteCuisine: any = createAsyncThunk<
  number, // ✅ return type: deleted id
  number // ✅ argument type: cuisine id
>('discountcoupons/deleteCuisine', async (id) => {
  try {
    const response = await apiService.patch(
      `soft_delete_cuisine_type?id=${id}`,
      {},
      PORT,
      MASTERURL,
    );
    return response;
  } catch (error: any) {
    return error?.message || 'An error occurred while deleting cuisine type';
  }
});

export const updateDiscountCoupons: any = createAsyncThunk(
  'discountcoupons/updateDiscountCoupons',
  async (params: any, thunkAPI) => {
    console.log(params);
    try {
      const response = await apiService.put('update_coupon', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const discountcouponsSlice = createSlice({
  name: 'discountcoupons',
  initialState: {
    data: [] as DiscountCoupon[],
    couponData: [],
    AvailableCouponsData: [],
    loading: false,
    error: null,
  } as DiscountCouponState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDiscountCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDiscountCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchDiscountCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(fetchFoodMenuForCoupon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFoodMenuForCoupon.fulfilled, (state, action) => {
        state.loading = false;
        state.couponData = action.payload; // array
      })
      .addCase(fetchFoodMenuForCoupon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch food menu';
      })
      .addCase(fetchAvailableCouponsForCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableCouponsForCart.fulfilled, (state, action) => {
        state.loading = false;
        state.AvailableCouponsData = action.payload; // DiscountCoupon[]
      })
      .addCase(fetchAvailableCouponsForCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch available coupons for cart';
      })
      .addCase(createDiscountCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDiscountCoupons.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(createDiscountCoupons.rejected, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateDiscountCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDiscountCoupons.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const updatedTable = action.payload;
      })
      .addCase(updateDiscountCoupons.rejected, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default discountcouponsSlice.reducer;
